import React from "react";
import { createRoot } from "react-dom/client";
import {
  CheckCircle2,
  Clock3,
  Download,
  Factory,
  Filter,
  HelpCircle,
  Leaf,
  List,
  LogIn,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  ShoppingCart,
  Trash2,
  X,
} from "lucide-react";
import BlendRunbook from "./BlendRunbook";
import OrderPage from "./OrderPage";
import "./styles.css";

const categories = ["Skin / Hair / Nails", "Immunity / Health", "Vitality / Energy / Mood"];
const saidBaseUrl = "https://accounts.sahasta.com";
const saidClientId = "hydro-terrace-microgreenlist";
const saidRedirectPath = "/auth/said/callback";
const colors = ["Green", "Reddish", "Purple"];
const growability = ["Strong", "Good"];
const evidenceLevels = ["High", "Moderate", "Limited"];
const purchaseStatuses = ["Buy now", "Future/test", "Hold", "Not in current plan"];
const appViews = {
  list: { path: "/", title: "Microgreen operating list" },
  assembly: { path: "/assembly", title: "Assembly workshop" },
  order: { path: "/order", title: "Quick order" },
};
const editableFields = [
  ["microgreen", "Microgreen"],
  ["scientific_name", "Scientific name"],
  ["family", "Family"],
  ["dominant_color", "Dominant color"],
  ["color_notes", "Color notes"],
  ["category", "Category"],
  ["secondary_category", "Secondary category"],
  ["sourceability", "Sourceability"],
  ["hydroponic_growability", "Hydroponic growability"],
  ["hydroponic_filter_note", "Hydroponic filter note"],
  ["harvest_window_days", "Harvest window"],
  ["harvest_stage", "Harvest stage"],
  ["harvest_notes", "Harvest notes"],
  ["hydro_seed_paper_fit", "Hydro seed-paper fit"],
  ["expected_height_inches", "Expected height"],
  ["key_nutrients_or_compounds", "Key nutrients / compounds"],
  ["evidence_summary", "Evidence summary"],
  ["evidence_level", "Evidence level"],
  ["caution", "Caution"],
  ["probable_sourcing", "Probable sourcing"],
  ["source_urls", "Source URLs"],
  ["purchase_status", "Purchase status"],
  ["purchase_item", "Purchase item"],
  ["purchase_package", "Purchase package"],
  ["purchase_price_usd", "Purchase price"],
  ["purchase_url", "Purchase URL"],
  ["purchase_role", "Purchase role"],
  ["purchase_mix_uses", "Purchase mix uses"],
  ["purchase_source_status", "Purchase source status"],
  ["purchase_risk", "Purchase risk"],
];

function apiHeaders(token) {
  return {
    "content-type": "application/json",
    "authorization": `Bearer ${token}`,
    "x-hydro-terrace-said-token": token,
  };
}

function base64Url(bytes) {
  const binary = Array.from(new Uint8Array(bytes), (byte) => String.fromCharCode(byte)).join("");
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function randomToken(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function pkceChallenge(verifier) {
  const bytes = new TextEncoder().encode(verifier);
  return base64Url(await crypto.subtle.digest("SHA-256", bytes));
}

function emptyRow() {
  return {
    microgreen: "",
    scientific_name: "",
    family: "",
    dominant_color: "Green",
    color_notes: "",
    category: "Immunity / Health",
    secondary_category: "Skin / Hair / Nails",
    probable_sourcing: "",
    sourceability: "Easy",
    hydroponic_growability: "Good",
    hydroponic_filter_note: "Pass: hydroponic-capable with controls.",
    harvest_window_days: "7-14",
    harvest_stage: "Microgreen stage",
    harvest_notes: "Validate against Hydro Terrace grow settings.",
    hydro_seed_paper_fit: "",
    expected_height_inches: "1.5-3",
    key_nutrients_or_compounds: "",
    evidence_summary: "",
    evidence_level: "Limited",
    caution: "",
    source_urls: "",
    evidence_source_ids: "",
    purchase_status: "Not in current plan",
    purchase_item: "",
    purchase_package: "",
    purchase_price_usd: "",
    purchase_url: "",
    purchase_role: "",
    purchase_mix_uses: "",
    purchase_source_status: "",
    purchase_risk: "",
  };
}

function Badge({ value, tone }) {
  return <span className={`badge ${tone || String(value).toLowerCase().replaceAll(" ", "-").replaceAll("/", "")}`}>{value}</span>;
}

function unansweredCount(item) {
  const questions = Array.isArray(item?.questions) ? item.questions : [];
  const answers = item?.answers || {};
  return questions.filter((question) => !String(answers[question.id] || answers[`${question.id}_other`] || "").trim()).length;
}

function purchaseTone(status) {
  if (status === "Buy now") return "buy-now";
  if (status === "Future/test") return "future-test";
  if (status === "Hold") return "hold";
  return "not-planned";
}

function viewFromPath(pathname) {
  if (pathname.startsWith(appViews.assembly.path)) return "assembly";
  if (pathname.startsWith(appViews.order.path)) return "order";
  return "list";
}

function App() {
  const [said, setSaid] = React.useState(() => localStorage.getItem("hydro-terrace-said-token") || localStorage.getItem("hydro-terrace-said") || "");
  const [saidDraft, setSaidDraft] = React.useState(said);
  const [authUser, setAuthUser] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [filters, setFilters] = React.useState({ search: "", category: "All", color: "All", hydro: "All", purchase: "All" });
  const [activeView, setActiveView] = React.useState(() => viewFromPath(window.location.pathname));
  const [queue, setQueue] = React.useState([]);
  const [notifications, setNotifications] = React.useState([]);
  const [prompt, setPrompt] = React.useState("");
  const [queueDrafts, setQueueDrafts] = React.useState({});
  const [questionPrompt, setQuestionPrompt] = React.useState(null);
  const [questionAnswers, setQuestionAnswers] = React.useState({});
  const loginCompletionRef = React.useRef(false);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const promptNeedingAnswers = queue.find((item) => Array.isArray(item.questions) && item.questions.length > 0 && unansweredCount(item) > 0);

  async function loadAll(activeSaID = said) {
    if (!activeSaID) return;
    setLoading(true);
    try {
      const [authRes, microRes, queueRes] = await Promise.all([
        fetch("/api/auth/validate", { headers: apiHeaders(activeSaID) }),
        fetch("/api/microgreens", { headers: apiHeaders(activeSaID) }),
        fetch("/api/prompts", { headers: apiHeaders(activeSaID) }),
      ]);
      if (!authRes.ok) throw new Error((await authRes.json()).error || "Could not validate HydroTerrace account.");
      if (!microRes.ok) throw new Error((await microRes.json()).error || "Could not load microgreens.");
      if (!queueRes.ok) throw new Error((await queueRes.json()).error || "Could not load prompt queue.");
      const authData = await authRes.json();
      const microData = await microRes.json();
      const queueData = await queueRes.json();
      setAuthUser(authData.user);
      setRows(microData.rows);
      setQueue(queueData.prompts);
      setNotifications(queueData.notifications);
      if (!selectedId && microData.rows[0]) {
        setSelectedId(microData.rows[0].id);
        setDraft(microData.rows[0]);
      }
      return true;
    } catch (error) {
      setMessage(error.message);
      if (/token|credential|@hydroterrace\.com|account/i.test(error.message)) {
        setAuthUser(null);
        setSaid("");
        localStorage.removeItem("hydro-terrace-said-token");
        localStorage.removeItem("hydro-terrace-said");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (said) loadAll(said);
    const interval = setInterval(() => {
      if (said) loadAll(said);
    }, 45000);
    return () => clearInterval(interval);
  }, [said]);

  React.useEffect(() => {
    if (loginCompletionRef.current || window.location.pathname !== saidRedirectPath) return;
    loginCompletionRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const returnedState = params.get("state");
    const error = params.get("error");
    if (error) {
      setMessage(`SaID login failed: ${error}`);
      window.history.replaceState({}, document.title, "/");
      return;
    }
    if (!code || !returnedState) {
      setMessage("SaID login did not return a usable authorization code.");
      window.history.replaceState({}, document.title, "/");
      return;
    }

    setLoading(true);
    completeSaIDLogin(code, returnedState)
      .catch((loginError) => {
        setMessage(loginError.message);
        window.history.replaceState({}, document.title, "/");
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (selected) setDraft(selected);
  }, [selectedId, rows.length]);

  React.useEffect(() => {
    const handlePopState = () => setActiveView(viewFromPath(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  function navigate(view) {
    const path = appViews[view].path;
    if (window.location.pathname !== path) window.history.pushState({}, document.title, path);
    setActiveView(view);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function activateSaID(event) {
    event.preventDefault();
    const next = saidDraft.trim();
    if (!next) return;
    setMessage("");
    const ok = await loadAll(next);
    if (!ok) return;
    localStorage.setItem("hydro-terrace-said-token", next);
    localStorage.removeItem("hydro-terrace-said");
    setSaid(next);
  }

  async function startSaIDLogin() {
    const verifier = randomToken(64);
    const state = randomToken(24);
    const authorize = new URL("/authorize", saidBaseUrl);
    authorize.searchParams.set("client_id", saidClientId);
    authorize.searchParams.set("redirect_uri", `${window.location.origin}${saidRedirectPath}`);
    authorize.searchParams.set("response_type", "code");
    authorize.searchParams.set("state", state);
    authorize.searchParams.set("scope", "profile email");
    authorize.searchParams.set("code_challenge", await pkceChallenge(verifier));
    authorize.searchParams.set("code_challenge_method", "S256");
    sessionStorage.setItem("hydro-terrace-said-pkce", JSON.stringify({ verifier, state }));
    sessionStorage.setItem("hydro-terrace-said-return-path", window.location.pathname);
    window.location.assign(authorize.toString());
  }

  async function completeSaIDLogin(code, returnedState) {
    const stored = JSON.parse(sessionStorage.getItem("hydro-terrace-said-pkce") || "{}");
    sessionStorage.removeItem("hydro-terrace-said-pkce");
    if (!stored.verifier || !stored.state || stored.state !== returnedState) {
      throw new Error("SaID login state could not be verified.");
    }

    const response = await fetch(`${saidBaseUrl}/api/oauth/token`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        grant_type: "authorization_code",
        client_id: saidClientId,
        redirect_uri: `${window.location.origin}${saidRedirectPath}`,
        code,
        code_verifier: stored.verifier,
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false || !payload?.data?.access_token) {
      throw new Error(payload?.error?.message || "SaID token exchange failed.");
    }

    const token = payload.data.access_token;
    const ok = await loadAll(token);
    if (!ok) return;
    localStorage.setItem("hydro-terrace-said-token", token);
    localStorage.removeItem("hydro-terrace-said");
    setSaidDraft(token);
    setSaid(token);
    const returnPath = sessionStorage.getItem("hydro-terrace-said-return-path") || "/";
    sessionStorage.removeItem("hydro-terrace-said-return-path");
    window.history.replaceState({}, document.title, returnPath);
    setActiveView(viewFromPath(returnPath));
  }

  function signOut() {
    setSaid("");
    setSaidDraft("");
    setAuthUser(null);
    setRows([]);
    setQueue([]);
    setNotifications([]);
    localStorage.removeItem("hydro-terrace-said-token");
    localStorage.removeItem("hydro-terrace-said");
  }

  async function downloadCsv() {
    setLoading(true);
    try {
      const res = await fetch("/api/export", { headers: apiHeaders(said) });
      if (!res.ok) throw new Error((await res.json()).error || "Could not download CSV.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "hydro-terrace-microgreens.csv";
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredRows = rows.filter((row) => {
    const search = filters.search.toLowerCase();
    const haystack = [
      row.microgreen,
      row.scientific_name,
      row.family,
      row.expected_height_inches,
      row.key_nutrients_or_compounds,
      row.caution,
      row.purchase_status,
      row.purchase_item,
      row.purchase_role,
      row.purchase_mix_uses,
      row.purchase_risk,
    ].join(" ").toLowerCase();
    return (
      (!search || haystack.includes(search)) &&
      (filters.category === "All" || row.category === filters.category) &&
      (filters.color === "All" || row.dominant_color === filters.color) &&
      (filters.hydro === "All" || row.hydroponic_growability === filters.hydro) &&
      (filters.purchase === "All" || row.purchase_status === filters.purchase)
    );
  });

  const stats = React.useMemo(() => {
    const count = (field, value) => rows.filter((row) => row[field] === value).length;
    return {
      total: rows.length,
      strong: count("hydroponic_growability", "Strong"),
      good: count("hydroponic_growability", "Good"),
      green: count("dominant_color", "Green"),
      reddish: count("dominant_color", "Reddish"),
      purple: count("dominant_color", "Purple"),
    };
  }, [rows]);

  async function saveDraft() {
    if (!draft?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/microgreens/${draft.id}`, {
        method: "PATCH",
        headers: apiHeaders(said),
        body: JSON.stringify({ updates: draft }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Could not save row.");
      const data = await res.json();
      setRows((current) => current.map((row) => (row.id === draft.id ? data.row : row)));
      setMessage(`Saved ${data.row.microgreen}.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function createRow() {
    setLoading(true);
    try {
      const res = await fetch("/api/microgreens", {
        method: "POST",
        headers: apiHeaders(said),
        body: JSON.stringify({ row: emptyRow() }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Could not create row.");
      const data = await res.json();
      setRows((current) => [...current, data.row]);
      setSelectedId(data.row.id);
      setDraft(data.row);
      setMessage("New row created.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function deleteSelected() {
    if (!selectedId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/microgreens/${selectedId}`, {
        method: "DELETE",
        headers: apiHeaders(said),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Could not delete row.");
      setRows((current) => current.filter((row) => row.id !== selectedId));
      setSelectedId(null);
      setDraft(null);
      setMessage("Row deleted.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function submitPrompt(event) {
    event.preventDefault();
    if (!prompt.trim()) return;
    setLoading(true);
    try {
      const res = await fetch("/api/prompts", {
        method: "POST",
        headers: apiHeaders(said),
        body: JSON.stringify({ prompt }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Could not queue prompt.");
      setPrompt("");
      setMessage("Prompt queued.");
      await loadAll(said);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function updatePromptStatus(id, status) {
    const local = queueDrafts[id] || {};
    setLoading(true);
    try {
      const res = await fetch(`/api/prompts/${id}`, {
        method: "PATCH",
        headers: apiHeaders(said),
        body: JSON.stringify({ status, summary: local.summary || "" }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Could not update prompt.");
      await loadAll(said);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  function openQuestions(item) {
    setQuestionPrompt(item);
    setQuestionAnswers(item.answers || {});
  }

  async function saveQuestionAnswers(event) {
    event.preventDefault();
    if (!questionPrompt) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/prompts/${questionPrompt.id}`, {
        method: "PATCH",
        headers: apiHeaders(said),
        body: JSON.stringify({
          answers: questionAnswers,
          summary: `Answers saved for Prompt #${questionPrompt.id}.`,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Could not save answers.");
      setQuestionPrompt(null);
      setQuestionAnswers({});
      setMessage("Answers saved.");
      await loadAll(said);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  if (!said) {
    return (
      <main className="gate">
        <section className="gatePanel">
          <div className="brandMark">
            <Leaf size={30} />
            <span>Hydro Terrace</span>
          </div>
          <h1>Microgreen list</h1>
          <div className="saidForm">
            <small className="gateHint">Access requires a verified @hydroterrace.com email attached to the SaID account.</small>
            {message && <strong className="gateError">{message}</strong>}
            <button type="button" onClick={startSaIDLogin} disabled={loading}>
              <LogIn size={17} />
              {loading ? "Validating" : "Continue with SaID"}
            </button>
            <details className="tokenDetails">
              <summary>Use access token</summary>
              <form onSubmit={activateSaID}>
                <label htmlFor="said">SaID app access token</label>
                <input id="said" type="password" value={saidDraft} onChange={(event) => setSaidDraft(event.target.value)} />
                <button type="submit" disabled={loading}>{loading ? "Validating" : "Enter"}</button>
              </form>
            </details>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="appShell">
      <header className="topbar">
        <div>
          <div className="brandLine">
            <Leaf size={22} />
            <span>Hydro Terrace</span>
          </div>
          <h1>{appViews[activeView].title}</h1>
        </div>
        <div className="topActions">
          <span className="saidChip">{authUser?.email || "HydroTerrace verified"}</span>
          <button className="iconButton" onClick={() => loadAll(said)} disabled={loading} title="Refresh">
            <RefreshCw size={18} />
          </button>
          <a className="iconButton" href="/api/export" onClick={(event) => {
            event.preventDefault();
            downloadCsv();
          }} title="Download CSV">
            <Download size={18} />
          </a>
          <button className="iconButton" onClick={signOut} title="Sign out">
            <X size={18} />
          </button>
        </div>
      </header>

      <nav className="appNav" aria-label="Microgreen tools">
        <button type="button" className={activeView === "list" ? "active" : ""} onClick={() => navigate("list")}>
          <List size={17} /> Microgreen list
        </button>
        <button type="button" className={activeView === "assembly" ? "active" : ""} onClick={() => navigate("assembly")}>
          <Factory size={17} /> Assembly
        </button>
        <button type="button" className={activeView === "order" ? "active" : ""} onClick={() => navigate("order")}>
          <ShoppingCart size={17} /> Quick order
        </button>
      </nav>

      {message && (
        <div className="toast">
          <CheckCircle2 size={18} />
          <span>{message}</span>
          <button onClick={() => setMessage("")}><X size={16} /></button>
        </div>
      )}

      {notifications.length > 0 && (
        <section className="notifications">
          {notifications.slice(0, 3).map((note) => (
            <div key={note.id}>{note.message}</div>
          ))}
        </section>
      )}

      {promptNeedingAnswers && (
        <section className="questionStrip">
          <div>
            <strong>Prompt #{promptNeedingAnswers.id} needs direction</strong>
            <span>{unansweredCount(promptNeedingAnswers)} unanswered question(s)</span>
          </div>
          <button onClick={() => openQuestions(promptNeedingAnswers)}><HelpCircle size={16} /> Answer questions</button>
        </section>
      )}

      {activeView === "list" && (
        <>
          <section className="statsRail">
            <div><span>{stats.total}</span><label>Rows</label></div>
            <div><span>{stats.strong}</span><label>Strong hydro</label></div>
            <div><span>{stats.good}</span><label>Good hydro</label></div>
            <div><span>{stats.green}</span><label>Green</label></div>
            <div><span>{stats.reddish}</span><label>Reddish</label></div>
            <div><span>{stats.purple}</span><label>Purple</label></div>
          </section>

          <section className="workspace">
        <div className="tablePane">
          <div className="toolbar">
            <div className="searchBox">
              <Search size={17} />
              <input placeholder="Search crops, compounds, cautions" value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} />
            </div>
            <label className="selectLabel">
              <Filter size={15} />
              <select value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}>
                <option>All</option>
                {categories.map((category) => <option key={category}>{category}</option>)}
              </select>
            </label>
            <select value={filters.color} onChange={(event) => setFilters({ ...filters, color: event.target.value })}>
              <option>All</option>
              {colors.map((color) => <option key={color}>{color}</option>)}
            </select>
            <select value={filters.hydro} onChange={(event) => setFilters({ ...filters, hydro: event.target.value })}>
              <option>All</option>
              {growability.map((item) => <option key={item}>{item}</option>)}
            </select>
            <select value={filters.purchase} onChange={(event) => setFilters({ ...filters, purchase: event.target.value })}>
              <option>All</option>
              {purchaseStatuses.map((item) => <option key={item}>{item}</option>)}
            </select>
            <button className="primarySmall" onClick={createRow}><Plus size={16} /> Row</button>
          </div>

          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Crop</th>
                  <th>Buy plan</th>
                  <th>Color</th>
                  <th>Category</th>
                  <th>Hydro</th>
                  <th>Harvest</th>
                  <th>Height</th>
                  <th>Evidence</th>
                  <th>Caution</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} className={row.id === selectedId ? "activeRow" : ""} onClick={() => setSelectedId(row.id)}>
                    <td>
                      <strong>{row.microgreen}</strong>
                      <small>{row.scientific_name}</small>
                    </td>
                    <td>
                      <Badge value={row.purchase_status || "Not in current plan"} tone={purchaseTone(row.purchase_status)} />
                      {row.purchase_item && <small>{row.purchase_item}</small>}
                    </td>
                    <td><Badge value={row.dominant_color} /></td>
                    <td>{row.category}</td>
                    <td><Badge value={row.hydroponic_growability} tone={row.hydroponic_growability === "Strong" ? "strong" : "good"} /></td>
                    <td>{row.harvest_window_days} days</td>
                    <td>{row.expected_height_inches} in</td>
                    <td><Badge value={row.evidence_level} /></td>
                    <td className="cautionCell">{row.caution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="editorPane">
          <div className="panelHeader">
            <div>
              <span className="eyebrow">Editable row</span>
              <h2>{draft?.microgreen || "Select a row"}</h2>
            </div>
            <div className="panelActions">
              <button className="iconButton danger" onClick={deleteSelected} disabled={!draft} title="Delete">
                <Trash2 size={17} />
              </button>
              <button className="primarySmall" onClick={saveDraft} disabled={!draft || loading}><Save size={16} /> Save</button>
            </div>
          </div>
          {draft ? (
            <div className="fieldGrid">
              {editableFields.map(([field, label]) => {
                const value = draft[field] ?? "";
                if (field === "dominant_color") {
                  return <SelectField key={field} label={label} value={value} options={colors} onChange={(next) => setDraft({ ...draft, [field]: next })} />;
                }
                if (field === "category" || field === "secondary_category") {
                  return <SelectField key={field} label={label} value={value} options={categories} onChange={(next) => setDraft({ ...draft, [field]: next })} />;
                }
                if (field === "hydroponic_growability") {
                  return <SelectField key={field} label={label} value={value} options={growability} onChange={(next) => setDraft({ ...draft, [field]: next })} />;
                }
                if (field === "evidence_level") {
                  return <SelectField key={field} label={label} value={value} options={evidenceLevels} onChange={(next) => setDraft({ ...draft, [field]: next })} />;
                }
                if (field === "purchase_status") {
                  return <SelectField key={field} label={label} value={value || "Not in current plan"} options={purchaseStatuses} onChange={(next) => setDraft({ ...draft, [field]: next })} />;
                }
                return (
                  <label key={field} className={value.length > 90 ? "wideField" : ""}>
                    <span>{label}</span>
                    {value.length > 90 || field.includes("summary") || field.includes("urls") || field.includes("note") || field === "caution" ? (
                      <textarea value={value} onChange={(event) => setDraft({ ...draft, [field]: event.target.value })} />
                    ) : (
                      <input value={value} onChange={(event) => setDraft({ ...draft, [field]: event.target.value })} />
                    )}
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="emptyState">Select a row to edit.</div>
          )}
        </aside>
          </section>

          <section className="queuePanel">
        <div className="queueComposer">
          <div>
            <span className="eyebrow">FIFO change queue</span>
            <h2>Prompt field</h2>
          </div>
          <form onSubmit={submitPrompt}>
            <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="Request a data, design, deployment, or code change." />
            <button className="primarySmall" type="submit"><Send size={16} /> Queue prompt</button>
          </form>
        </div>
        <div className="queueList">
          {queue.slice(0, 8).map((item) => (
            <article key={item.id} className="queueItem">
              <header>
                <span>#{item.id}</span>
                <Badge value={item.status} tone={item.status} />
              </header>
              <p>{item.prompt}</p>
              <textarea
                value={queueDrafts[item.id]?.summary ?? item.summary ?? ""}
                onChange={(event) => setQueueDrafts({ ...queueDrafts, [item.id]: { summary: event.target.value } })}
                placeholder="Summary notification"
              />
              <div className="queueActions">
                {Array.isArray(item.questions) && item.questions.length > 0 && (
                  <button onClick={() => openQuestions(item)}><HelpCircle size={14} /> Answer questions</button>
                )}
                <button onClick={() => updatePromptStatus(item.id, "in_progress")}><Clock3 size={14} /> In progress</button>
                <button onClick={() => updatePromptStatus(item.id, "completed")}><CheckCircle2 size={14} /> Completed</button>
              </div>
            </article>
          ))}
        </div>
          </section>
        </>
      )}

      {activeView === "assembly" && <BlendRunbook />}
      {activeView === "order" && <OrderPage rows={rows} onMessage={setMessage} />}

      {questionPrompt && (
        <div className="modalBackdrop" role="dialog" aria-modal="true" aria-labelledby="questions-title">
          <form className="questionModal" onSubmit={saveQuestionAnswers}>
            <header>
              <div>
                <span className="eyebrow">Prompt #{questionPrompt.id}</span>
                <h2 id="questions-title">Answer questions</h2>
              </div>
              <button type="button" className="iconButton" onClick={() => setQuestionPrompt(null)} title="Close">
                <X size={18} />
              </button>
            </header>
            <p>{questionPrompt.prompt}</p>
            <div className="questionList">
              {(questionPrompt.questions || []).map((question) => (
                <fieldset key={question.id}>
                  <legend>{question.label}</legend>
                  <div className="optionGrid">
                    {(question.options || []).map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={questionAnswers[question.id] === option ? "selectedOption" : ""}
                        onClick={() => setQuestionAnswers({ ...questionAnswers, [question.id]: option })}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  <input
                    value={questionAnswers[`${question.id}_other`] || ""}
                    onChange={(event) => setQuestionAnswers({ ...questionAnswers, [`${question.id}_other`]: event.target.value })}
                    placeholder="Other answer or notes"
                  />
                </fieldset>
              ))}
            </div>
            <footer>
              <button type="button" onClick={() => setQuestionPrompt(null)}>Cancel</button>
              <button className="primarySmall" type="submit" disabled={loading}><Save size={16} /> Save answers</button>
            </footer>
          </form>
        </div>
      )}
    </main>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}

createRoot(document.getElementById("root")).render(<App />);
